import { isVerticalTextOrientation } from "./textOrientation.js";

export const HIGHLIGHT_DEFAULT_STROKE_WIDTH = 2;
export const HIGHLIGHT_DEFAULT_BACKGROUND_PADDING = 4;

/**
 * Typography overrides ride along with the paint fields but collapse into a
 * single "font" token when the slot is summarized as a produce column.
 */
export const isHighlightTypographyField = (key) =>
  key === "fontSelection" ||
  key === "fontAssetRef" ||
  key === "fontFaceRef" ||
  key === "fontWeight" ||
  key === "textStyle";

export const isHighlightStyleField = (key) =>
  key === "color" ||
  key === "strokeColor" ||
  key === "strokeWidth" ||
  key === "backgroundColor" ||
  key === "backgroundPadding" ||
  key === "backgroundRadius" ||
  isHighlightTypographyField(key);

/**
 * Any stored textStyle counts, including "normal": it is how a highlight
 * cancels emphasis the layer applies to the rest of the text.
 */
export const highlightHasTypography = (slot) =>
  Boolean(String(slot?.fontAssetRef || "").trim()) ||
  Number(slot?.fontWeight) > 0 ||
  Boolean(String(slot?.textStyle || "").trim());

export const canHighlightTextSource = (kind) =>
  kind === "data" || kind === "fixed";

export const highlightSourceText = (node) => {
  if (!node) return "";
  if (node.source?.kind === "fixed") {
    return String(node.source?.value || node.originText || "");
  }
  return String(node.originText || "");
};

const COLOR_PALETTE = [
  { slug: "negro", hex: "#000000" },
  { slug: "gris", hex: "#808080" },
  { slug: "blanco", hex: "#FFFFFF" },
  { slug: "rojo", hex: "#FF0000" },
  { slug: "naranja", hex: "#FF8800" },
  { slug: "amarillo", hex: "#FFD400" },
  { slug: "verde", hex: "#22C55E" },
  { slug: "cian", hex: "#00FFFF" },
  { slug: "azul", hex: "#2563EB" },
  { slug: "morado", hex: "#7C3AED" },
  { slug: "rosa", hex: "#EC4899" },
  { slug: "marron", hex: "#8B4513" },
  { slug: "beige", hex: "#F5DEB3" },
];

export const createHighlightId = () =>
  `hl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export const normalizeHighlightSlot = (slot) => {
  if (!slot || typeof slot !== "object") return null;
  if (!slot.kind) {
    return { ...slot };
  }
  const next = {
    id: slot.id,
    example: slot.example || "",
  };
  // Typography is orthogonal to the legacy paint `kind`, so it survives it.
  if (slot.fontAssetRef) next.fontAssetRef = slot.fontAssetRef;
  if (slot.fontFaceRef) next.fontFaceRef = slot.fontFaceRef;
  if (slot.fontWeight != null) next.fontWeight = slot.fontWeight;
  if (slot.textStyle) next.textStyle = slot.textStyle;
  if (slot.kind === "fill" && slot.color) next.color = slot.color;
  if (slot.kind === "stroke") {
    if (slot.color) next.strokeColor = slot.color;
    if (slot.strokeWidth != null) next.strokeWidth = slot.strokeWidth;
  }
  if (slot.kind === "background") {
    if (slot.color) next.backgroundColor = slot.color;
    if (slot.backgroundPadding != null) {
      next.backgroundPadding = slot.backgroundPadding;
    }
    if (slot.backgroundRadius != null) {
      next.backgroundRadius = slot.backgroundRadius;
    }
  }
  if (slot.kind === "fill" || slot.kind === "stroke" || slot.kind === "background") {
    return next;
  }
  const exhaustive = slot.kind;
  throw new Error(`Unhandled highlight kind: ${exhaustive}`);
};

export const highlightHasStyle = (slot) => {
  const next = normalizeHighlightSlot(slot);
  if (!next) return false;
  const hasFill = Boolean(String(next.color || "").trim());
  const hasStroke =
    Boolean(String(next.strokeColor || "").trim()) &&
    Number(next.strokeWidth) > 0;
  const hasBackground =
    Boolean(String(next.backgroundColor || "").trim()) &&
    Number(next.backgroundPadding) > 0;
  return hasFill || hasStroke || hasBackground || highlightHasTypography(next);
};

/** Leftover stroke/background color after px was cleared, while the phrase is still selected. */
export const highlightHasDraftPaint = (slot) => {
  const next = normalizeHighlightSlot(slot);
  if (!next) return false;
  return (
    Boolean(String(next.strokeColor || "").trim()) ||
    Boolean(String(next.backgroundColor || "").trim())
  );
};

export const highlightShouldKeepSlot = (slot) =>
  highlightHasStyle(slot) || highlightHasDraftPaint(slot);

export const findHighlightByPhrase = ({ highlights = [], phrase } = {}) => {
  const needle = String(phrase || "").trim();
  if (!needle) return null;
  return (
    (Array.isArray(highlights) ? highlights : [])
      .map(normalizeHighlightSlot)
      .find((slot) => String(slot?.example || "").trim() === needle) || null
  );
};

const parseHexRgb = (value) => {
  const raw = String(value || "")
    .trim()
    .replace(/^#/, "");
  const hex =
    raw.length === 3
      ? raw
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
};

export const colorSlugFromHex = (value) => {
  const rgb = parseHexRgb(value);
  if (!rgb) return "gris";
  let best = COLOR_PALETTE[0];
  let bestDistance = Number.POSITIVE_INFINITY;
  COLOR_PALETTE.forEach((swatch) => {
    const next = parseHexRgb(swatch.hex);
    if (!next) return;
    const distance =
      (rgb.r - next.r) ** 2 + (rgb.g - next.g) ** 2 + (rgb.b - next.b) ** 2;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = swatch;
    }
  });
  return best.slug;
};

/**
 * Structural comparison for the layer-style equality checks that decide
 * whether an edit is a real change. They enumerate style fields one by one, so
 * highlights need an explicit comparison or a highlight edit reads as a no-op.
 */
export const areHighlightListsEqual = (left, right) => {
  const a = Array.isArray(left) ? left : [];
  const b = Array.isArray(right) ? right : [];
  if (a.length !== b.length) return false;
  return a.every((slot, index) => {
    const other = b[index];
    if (!slot || !other) return slot === other;
    const keys = new Set([...Object.keys(slot), ...Object.keys(other)]);
    return [...keys].every((key) => (slot[key] ?? "") === (other[key] ?? ""));
  });
};

/**
 * Widest pill padding the layer can paint — its own fill, or any highlight's.
 *
 * A line flush against the layer box paints its pill this far outside it, so
 * both painters open their clip up by this much: the background stays whole
 * while the text keeps the alignment it was given.
 */
export const maxTextBackgroundPadding = ({
  backgroundPadding = 0,
  highlights = [],
} = {}) => {
  const base = Number(backgroundPadding);
  let widest = Number.isFinite(base) && base > 0 ? base : 0;
  listActiveHighlights(highlights).forEach((slot) => {
    if (!String(slot.backgroundColor || "").trim()) return;
    const padding = Number(slot.backgroundPadding);
    if (Number.isFinite(padding) && padding > widest) widest = padding;
  });
  return widest;
};

export const listActiveHighlights = (highlights = []) => {
  if (!Array.isArray(highlights)) return [];
  return highlights
    .map(normalizeHighlightSlot)
    .filter(
      (slot) =>
        slot && String(slot.example || "").trim() && highlightHasStyle(slot)
    );
};

export const listSelectableHighlights = (highlights = []) => {
  if (!Array.isArray(highlights)) return [];
  return highlights
    .map(normalizeHighlightSlot)
    .filter(
      (slot) =>
        slot &&
        String(slot.example || "").trim() &&
        highlightShouldKeepSlot(slot)
    );
};

export const pruneInactiveHighlights = (highlights = []) =>
  listActiveHighlights(highlights)
    .map((slot) => {
      const next = { ...slot };
      if (!(Number(next.strokeWidth) > 0)) {
        delete next.strokeColor;
        delete next.strokeWidth;
      }
      if (!(Number(next.backgroundPadding) > 0)) {
        delete next.backgroundColor;
        delete next.backgroundPadding;
        delete next.backgroundRadius;
      }
      return next;
    })
    .filter(highlightHasStyle);

const highlightColumnSuffix = (slot) => {
  const parts = [];
  if (String(slot.color || "").trim()) {
    parts.push(`letra_${colorSlugFromHex(slot.color)}`);
  }
  if (String(slot.strokeColor || "").trim() && Number(slot.strokeWidth) > 0) {
    parts.push(`borde_${colorSlugFromHex(slot.strokeColor)}`);
  }
  if (
    String(slot.backgroundColor || "").trim() &&
    Number(slot.backgroundPadding) > 0
  ) {
    parts.push(`fondo_${colorSlugFromHex(slot.backgroundColor)}`);
  }
  // Family, weight and emphasis collapse into one token: the column name only
  // has to be recognizable, not to describe the whole typography.
  if (highlightHasTypography(slot)) {
    parts.push("font");
  }
  return parts.join("_");
};

export const listHighlightColumns = ({ variable, highlights, text } = {}) => {
  const keyBase = String(variable || "").trim();
  if (!keyBase) return [];
  const used = new Set();
  return sortHighlightsByTextOrder({
    highlights: listActiveHighlights(highlights),
    text,
  }).map((slot) => {
    const suffix = highlightColumnSuffix(slot) || "estilo";
    let key = `${keyBase}_${suffix}`;
    let index = 2;
    while (used.has(key)) {
      key = `${keyBase}_${suffix}_${index}`;
      index += 1;
    }
    used.add(key);
    return {
      key,
      label: key,
      type: "string",
      originExample: String(slot.example || "").trim(),
      role: "highlight",
      parentKey: keyBase,
      slot,
    };
  });
};

export const listHighlightColumnKeys = ({ variable, highlights, text } = {}) =>
  listHighlightColumns({ variable, highlights, text }).map(
    (column) => column.key
  );

export const phrasesFromHighlightColumns = ({ columns = [], row = {} } = {}) => {
  const phrases = {};
  columns.forEach((column) => {
    const id = column?.slot?.id;
    if (!id) return;
    phrases[id] = String(row?.[column.key] ?? "").trim();
  });
  return phrases;
};

const applyHighlightRadiusPatch = ({ current, value }) => {
  if (value === "" || value == null) {
    delete current.backgroundRadius;
    return;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    delete current.backgroundRadius;
    return;
  }
  current.backgroundRadius = parsed;
  const hadBackgroundPaint =
    Boolean(String(current.backgroundColor || "").trim()) ||
    Number(current.backgroundPadding) > 0;
  if (!hadBackgroundPaint) {
    current.backgroundColor = "#000000";
    current.backgroundPadding = HIGHLIGHT_DEFAULT_BACKGROUND_PADDING;
  }
};

const applyHighlightMetricPatch = ({
  current,
  metricKey,
  colorKey,
  value,
  defaultColor,
}) => {
  // Empty or 0 drops the px but keeps the color while the phrase is still
  // selected. The slot is pruned when that highlight selection goes away.
  if (value === "" || value == null) {
    delete current[metricKey];
    return;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    delete current[metricKey];
    return;
  }
  current[metricKey] = parsed;
  if (!String(current[colorKey] || "").trim()) {
    current[colorKey] = defaultColor;
  }
};

export const applyHighlightStylePatch = ({
  highlights = [],
  phrase,
  key,
  value,
  text,
} = {}) => {
  const nextPhrase = String(phrase || "").trim();
  if (!nextPhrase || !isHighlightStyleField(key)) {
    return Array.isArray(highlights) ? highlights : [];
  }
  const list = (Array.isArray(highlights) ? highlights : [])
    .map(normalizeHighlightSlot)
    .filter(Boolean);
  const index = list.findIndex(
    (slot) => String(slot.example || "").trim() === nextPhrase
  );
  const current =
    index >= 0
      ? { ...list[index] }
      : { id: createHighlightId(), example: nextPhrase };

  switch (key) {
    case "color": {
      const color = String(value || "").trim();
      if (color) current.color = color;
      else delete current.color;
      break;
    }
    case "strokeColor": {
      const color = String(value || "").trim();
      if (color) {
        current.strokeColor = color;
        if (!(Number(current.strokeWidth) > 0)) {
          current.strokeWidth = HIGHLIGHT_DEFAULT_STROKE_WIDTH;
        }
      } else {
        delete current.strokeColor;
        delete current.strokeWidth;
      }
      break;
    }
    case "strokeWidth": {
      applyHighlightMetricPatch({
        current,
        metricKey: "strokeWidth",
        colorKey: "strokeColor",
        value,
        defaultColor: "#000000",
      });
      break;
    }
    case "backgroundColor": {
      const color = String(value || "").trim();
      if (color) {
        current.backgroundColor = color;
        if (!(Number(current.backgroundPadding) > 0)) {
          current.backgroundPadding = HIGHLIGHT_DEFAULT_BACKGROUND_PADDING;
        }
      } else {
        delete current.backgroundColor;
        delete current.backgroundPadding;
        delete current.backgroundRadius;
      }
      break;
    }
    case "backgroundPadding": {
      applyHighlightMetricPatch({
        current,
        metricKey: "backgroundPadding",
        colorKey: "backgroundColor",
        value,
        defaultColor: "#000000",
      });
      break;
    }
    case "backgroundRadius": {
      applyHighlightRadiusPatch({ current, value });
      break;
    }
    case "fontSelection": {
      const selection = value && typeof value === "object" ? value : {};
      const family = String(selection.fontAssetRef || "").trim();
      const face = String(selection.fontFaceRef || "").trim();
      const weight = Number(selection.fontWeight);
      if (family) current.fontAssetRef = family;
      else delete current.fontAssetRef;
      if (face) current.fontFaceRef = face;
      else delete current.fontFaceRef;
      if (Number.isFinite(weight) && weight > 0) current.fontWeight = weight;
      else delete current.fontWeight;
      break;
    }
    case "fontAssetRef":
    case "fontFaceRef": {
      const next = String(value || "").trim();
      if (next) current[key] = next;
      else delete current[key];
      break;
    }
    case "fontWeight": {
      const parsed = Number(value);
      if (Number.isFinite(parsed) && parsed > 0) current.fontWeight = parsed;
      else delete current.fontWeight;
      break;
    }
    case "textStyle": {
      const next = String(value || "").trim();
      if (next) current.textStyle = next;
      else delete current.textStyle;
      break;
    }
    default: {
      const exhaustive = key;
      throw new Error(`Unhandled highlight style field: ${exhaustive}`);
    }
  }

  const finalize = (next) =>
    sortHighlightsByTextOrder({ highlights: next, text });

  if (!highlightShouldKeepSlot(current)) {
    return finalize(
      index >= 0 ? list.filter((_, itemIndex) => itemIndex !== index) : list
    );
  }
  if (index >= 0) {
    const next = [...list];
    next[index] = current;
    return finalize(next);
  }
  return finalize([...list, current]);
};

export const applyHighlightPhrases = ({ highlights = [], phrases = {} } = {}) => {
  if (!Array.isArray(highlights) || highlights.length === 0) return highlights;
  return highlights.map((slot) => {
    const phrase = String(phrases?.[slot?.id] ?? "").trim();
    if (!phrase) return slot;
    return { ...slot, example: phrase };
  });
};

export const normalizeHighlightPhrase = (value) =>
  String(value || "").replace(/\s+/g, " ").trim();

const collapseHighlightIndex = (value) => {
  const source = String(value || "");
  let collapsed = "";
  const indexMap = [];
  let pendingSpace = false;
  for (let index = 0; index < source.length; index += 1) {
    if (/\s/.test(source[index])) {
      if (collapsed.length) pendingSpace = true;
      continue;
    }
    if (pendingSpace) {
      collapsed += " ";
      indexMap.push(index);
      pendingSpace = false;
    }
    collapsed += source[index];
    indexMap.push(index);
  }
  return { collapsed, indexMap };
};

export const findAllOccurrences = ({ text, phrase } = {}) => {
  const needle = normalizeHighlightPhrase(phrase);
  if (!needle) return [];
  const { collapsed, indexMap } = collapseHighlightIndex(text);
  const ranges = [];
  let from = 0;
  while (from <= collapsed.length - needle.length) {
    const index = collapsed.indexOf(needle, from);
    if (index < 0) break;
    const start = indexMap[index];
    const last = indexMap[index + needle.length - 1];
    if (!Number.isFinite(start) || !Number.isFinite(last)) break;
    ranges.push({ start, end: last + 1 });
    from = index + needle.length;
  }
  return ranges;
};

/**
 * List highlights in REF order: first occurrence in `text` wins. Slots that
 * do not appear stay at the end, in their original relative order.
 */
export function sortHighlightsByTextOrder({ highlights = [], text = "" } = {}) {
  const source = String(text || "");
  const list = Array.isArray(highlights) ? highlights : [];
  if (!source || list.length < 2) return list;
  return [...list]
    .map((slot, index) => {
      const phrase = String(slot?.example || "").trim();
      const first = phrase
        ? findAllOccurrences({ text: source, phrase })[0]
        : null;
      return {
        slot,
        index,
        start: first?.start ?? Number.POSITIVE_INFINITY,
        end: first?.end ?? Number.POSITIVE_INFINITY,
      };
    })
    .sort((left, right) => {
      if (left.start !== right.start) return left.start - right.start;
      if (left.end !== right.end) return left.end - right.end;
      return left.index - right.index;
    })
    .map((item) => item.slot);
}

export const isHighlightPhraseInText = ({ text, phrase } = {}) =>
  findAllOccurrences({ text, phrase }).length > 0;

const findHighlightSlotBy = ({
  text,
  highlights = [],
  phrases = {},
  matches,
} = {}) => {
  const source = String(text || "");
  if (!source) return null;
  let match = null;
  listSelectableHighlights(highlights).forEach((slot) => {
    const phrase = String(phrases?.[slot.id] ?? slot.example ?? "").trim();
    if (!phrase) return;
    findAllOccurrences({ text: source, phrase }).forEach((range) => {
      if (matches(range)) match = slot;
    });
  });
  return match;
};

/**
 * Highlight the caret sits in, or null. The end of a run counts as inside so
 * that parking the caret right after a styled word still reports it, the way
 * a word processor keeps the active style while you keep typing.
 */
export const findHighlightAtOffset = ({
  text,
  highlights = [],
  phrases = {},
  offset,
} = {}) => {
  const index = Number(offset);
  if (!Number.isFinite(index)) return null;
  return findHighlightSlotBy({
    text,
    highlights,
    phrases,
    matches: ({ start, end }) => index >= start && index <= end,
  });
};

/**
 * Highlight that fully contains [start, end), or null. Selecting part of a
 * styled word ("om" inside "como") targets the highlight it belongs to rather
 * than starting a new one for the fragment. A range that spills past the
 * highlight is not contained, so it stays a new phrase.
 */
export const findHighlightContainingRange = ({
  text,
  highlights = [],
  phrases = {},
  start,
  end,
} = {}) => {
  const from = Number(start);
  const to = Number(end);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  return findHighlightSlotBy({
    text,
    highlights,
    phrases,
    matches: (range) => from >= range.start && to <= range.end,
  });
};

const paintStyleOrNull = (style) => {
  if (!style) return null;
  if (
    !style.fill &&
    !style.stroke &&
    !style.background &&
    !style.fontFamily &&
    !style.fontWeight &&
    !style.textStyle &&
    !style.selected
  ) {
    return null;
  }
  return style;
};

const styleKey = (style) => {
  const next = paintStyleOrNull(style);
  if (!next) return "";
  return [
    next.fill || "",
    next.stroke || "",
    next.strokeWidth ?? "",
    next.background || "",
    next.backgroundPadding ?? "",
    next.backgroundRadius ?? "",
    next.fontFamily || "",
    next.fontFaceRef || "",
    next.fontWeight ?? "",
    next.textStyle || "",
    next.selected ? "1" : "0",
  ].join("|");
};

const markSelectionOnChars = (charStyles, source, phrase) => {
  const needle = normalizeHighlightPhrase(phrase);
  const text = String(source || "");
  if (!needle || !text) return charStyles;
  const next =
    charStyles.length === text.length
      ? charStyles
      : Array.from({ length: text.length }, (_, index) => charStyles[index] || {});
  findAllOccurrences({ text, phrase: needle }).forEach(({ start, end }) => {
    for (let index = start; index < end; index += 1) {
      next[index] = { ...(next[index] || {}), selected: true };
    }
  });
  return next;
};

const applySlotToChar = (charStyle, slot) => {
  const next = { ...(charStyle || {}) };
  const normalized = normalizeHighlightSlot(slot) || {};
  if (String(normalized.color || "").trim()) {
    next.fill = normalized.color;
  }
  if (String(normalized.strokeColor || "").trim()) {
    next.stroke = normalized.strokeColor;
    const width = Number(normalized.strokeWidth);
    next.strokeWidth = Number.isFinite(width) && width > 0 ? width : 0;
  }
  if (String(normalized.backgroundColor || "").trim()) {
    next.background = normalized.backgroundColor;
    const padding = Number(normalized.backgroundPadding);
    next.backgroundPadding =
      Number.isFinite(padding) && padding > 0 ? padding : 0;
    if (normalized.backgroundRadius != null && normalized.backgroundRadius !== "") {
      const radius = Number(normalized.backgroundRadius);
      if (Number.isFinite(radius) && radius >= 0) {
        next.backgroundRadius = radius;
      }
    }
  }
  if (String(normalized.fontAssetRef || "").trim()) {
    next.fontFamily = normalized.fontAssetRef;
    // Carried for the canvas bake, which resolves the concrete face from it.
    if (String(normalized.fontFaceRef || "").trim()) {
      next.fontFaceRef = normalized.fontFaceRef;
    }
  }
  if (Number(normalized.fontWeight) > 0) {
    next.fontWeight = Number(normalized.fontWeight);
  }
  if (String(normalized.textStyle || "").trim()) {
    next.textStyle = normalized.textStyle;
  }
  return next;
};

export const resolveHighlightCharStyles = ({
  text,
  highlights = [],
  phrases = {},
} = {}) => {
  const source = String(text || "");
  const selectable = listSelectableHighlights(highlights);
  if (!source || !selectable.length) return [];
  const charStyles = Array.from({ length: source.length }, () => ({}));
  selectable.forEach((slot) => {
    const phrase = String(phrases?.[slot.id] ?? slot.example ?? "").trim();
    if (!phrase) return;
    findAllOccurrences({ text: source, phrase }).forEach(({ start, end }) => {
      for (let index = start; index < end; index += 1) {
        charStyles[index] = applySlotToChar(charStyles[index], slot);
      }
    });
  });
  return charStyles;
};

export const hasAnyPaintStyle = (charStyles = []) =>
  charStyles.some((style) => paintStyleOrNull(style));

export const listParagraphWords = (text) => {
  const source = String(text || "");
  const paragraphs = source.split("\n");
  let offset = 0;
  return paragraphs.map((paragraph, index) => {
    const words = [];
    const matcher = /\S+/g;
    let match = matcher.exec(paragraph);
    while (match) {
      words.push({
        text: match[0],
        start: offset + match.index,
        end: offset + match.index + match[0].length,
      });
      match = matcher.exec(paragraph);
    }
    const item = { words, start: offset, end: offset + paragraph.length };
    offset += paragraph.length + (index < paragraphs.length - 1 ? 1 : 0);
    return item;
  });
};

const styleForSourceRange = (charStyles, start, end) => {
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return null;
  }
  const first = paintStyleOrNull(charStyles[start]);
  const key = styleKey(first);
  for (let index = start + 1; index < end; index += 1) {
    if (styleKey(paintStyleOrNull(charStyles[index])) !== key) return null;
  }
  return first;
};

const mergeLineRuns = (chars = []) => {
  if (!chars.length) return [{ text: "", style: null }];
  const runs = [];
  chars.forEach(({ ch, style }) => {
    const last = runs[runs.length - 1];
    if (last && styleKey(last.style) === styleKey(style)) {
      last.text += ch;
      return;
    }
    runs.push({ text: ch, style });
  });
  return runs;
};

export const listWordHighlightRuns = ({ words = [], charStyles = [] } = {}) => {
  if (!Array.isArray(words) || words.length === 0) {
    return [{ text: "", style: null }];
  }
  const chars = [];
  words.forEach((word, index) => {
    const content = String(word?.text || "");
    const start = Number(word?.start) || 0;
    Array.from(content).forEach((ch, charIndex) => {
      chars.push({
        ch,
        style: paintStyleOrNull(charStyles[start + charIndex]),
      });
    });
    if (index < words.length - 1) {
      const upcoming = words[index + 1];
      chars.push({
        ch: " ",
        style: styleForSourceRange(
          charStyles,
          word.end,
          upcoming?.start ?? word.end
        ),
      });
    }
  });
  return mergeLineRuns(chars);
};

export const mapRunsToWrappedLines = ({ text, lines = [], charStyles = [] } = {}) => {
  const paragraphs = listParagraphWords(text);
  let para = 0;
  let wordIndex = 0;

  const nextWord = () => {
    while (para < paragraphs.length) {
      if (wordIndex < paragraphs[para].words.length) {
        const word = paragraphs[para].words[wordIndex];
        wordIndex += 1;
        return word;
      }
      para += 1;
      wordIndex = 0;
    }
    return null;
  };

  const peekWord = () => {
    let nextPara = para;
    let nextIndex = wordIndex;
    while (nextPara < paragraphs.length) {
      if (nextIndex < paragraphs[nextPara].words.length) {
        return paragraphs[nextPara].words[nextIndex];
      }
      nextPara += 1;
      nextIndex = 0;
    }
    return null;
  };

  return (Array.isArray(lines) ? lines : []).map((line) => {
    const content = String(line || "");
    if (!content) {
      if (para < paragraphs.length && paragraphs[para].words.length === 0) {
        para += 1;
        wordIndex = 0;
      }
      return [{ text: "", style: null }];
    }
    const lineWords = content.split(/\s+/).filter(Boolean);
    const chars = [];
    lineWords.forEach((word, index) => {
      const sourceWord = nextWord();
      if (!sourceWord) {
        Array.from(word).forEach((ch) => chars.push({ ch, style: null }));
      } else {
        Array.from(word).forEach((ch, charIndex) => {
          chars.push({
            ch,
            style: paintStyleOrNull(charStyles[sourceWord.start + charIndex]),
          });
        });
      }
      if (index < lineWords.length - 1) {
        const upcoming = peekWord();
        const gapStyle = sourceWord
          ? styleForSourceRange(
              charStyles,
              sourceWord.end,
              upcoming?.start ?? sourceWord.end
            )
          : null;
        chars.push({ ch: " ", style: gapStyle });
      }
    });
    return mergeLineRuns(chars);
  });
};

const compactWhitespace = (value) =>
  Array.from(String(value || ""))
    .filter((char) => !/\s/.test(char))
    .join("");

/**
 * Vertical paint drops whitespace, so a canvas selection is compact. Map it
 * back to the source slice, including the spaces the stack removed.
 */
export const expandCompactPhraseInText = ({ text, phrase } = {}) => {
  const source = String(text || "");
  const needle = compactWhitespace(phrase);
  if (!source || !needle) return "";
  const indexMap = [];
  const compactChars = [];
  Array.from(source).forEach((char, index) => {
    if (/\s/.test(char)) return;
    compactChars.push(char);
    indexMap.push(index);
  });
  const at = compactChars.join("").indexOf(needle);
  if (at < 0) return "";
  const start = indexMap[at];
  const last = indexMap[at + needle.length - 1];
  if (!Number.isFinite(start) || !Number.isFinite(last)) return "";
  return normalizeHighlightPhrase(source.slice(start, last + 1));
};

export const listLineHighlightRuns = ({
  text,
  lines = [],
  highlights = [],
  phrases = {},
  orientation = "horizontal",
  selectionPhrase = "",
} = {}) => {
  const selectable = listSelectableHighlights(highlights);
  const needle = String(selectionPhrase || "").trim();
  if (!selectable.length && !needle) return null;

  if (isVerticalTextOrientation(orientation)) {
    const compactText = compactWhitespace(text);
    const compactPhrases = {};
    const compactSlots = selectable.map((slot) => {
      const phrase = compactWhitespace(phrases?.[slot.id] ?? slot.example);
      compactPhrases[slot.id] = phrase;
      return { ...slot, example: phrase };
    });
    const charStyles = markSelectionOnChars(
      selectable.length
        ? resolveHighlightCharStyles({
            text: compactText,
            highlights: compactSlots,
            phrases: compactPhrases,
          })
        : Array.from({ length: compactText.length }, () => ({})),
      compactText,
      compactWhitespace(needle)
    );
    if (!hasAnyPaintStyle(charStyles)) return null;
    return (Array.isArray(lines) ? lines : []).map((glyph, index) => [
      {
        text: glyph || "",
        style: paintStyleOrNull(charStyles[index]),
      },
    ]);
  }

  const source = String(text || "");
  const charStyles = markSelectionOnChars(
    selectable.length
      ? resolveHighlightCharStyles({
          text: source,
          highlights: selectable,
          phrases,
        })
      : Array.from({ length: source.length }, () => ({})),
    source,
    needle
  );
  if (!hasAnyPaintStyle(charStyles)) return null;
  return mapRunsToWrappedLines({ text: source, lines, charStyles });
};

export const scaleHighlightMetrics = ({ highlights, scale } = {}) => {
  if (!Array.isArray(highlights) || highlights.length === 0) return highlights;
  const factor = Number(scale);
  if (!Number.isFinite(factor) || factor === 1) return highlights;
  return highlights.map((slot) => {
    const next = { ...slot };
    if (slot?.strokeWidth != null) {
      const parsed = Number(slot.strokeWidth);
      next.strokeWidth = Number.isFinite(parsed)
        ? Math.round(parsed * factor)
        : slot.strokeWidth;
    }
    if (slot?.backgroundPadding != null) {
      const parsed = Number(slot.backgroundPadding);
      next.backgroundPadding = Number.isFinite(parsed)
        ? Math.round(parsed * factor)
        : slot.backgroundPadding;
    }
    if (slot?.backgroundRadius != null) {
      const parsed = Number(slot.backgroundRadius);
      next.backgroundRadius = Number.isFinite(parsed)
        ? Math.round(parsed * factor)
        : slot.backgroundRadius;
    }
    return next;
  });
};

/**
 * Follow highlight phrases through a text edit. A highlight remembers the
 * phrase it paints, so editing a styled word ("hasta" -> "hast") would leave
 * the slot pointing at text that no longer exists and the paint would silently
 * vanish while the slot stayed listed in the inspector. The edit is read as a
 * single replaced span (common prefix/suffix), and every phrase overlapping it
 * is re-read from the new text. A slot is dropped only when its run was
 * deleted outright.
 *
 * Returns the rewritten list plus a map of old phrase -> new phrase, so a
 * standing text selection can follow the rename instead of being cleared.
 */
export const retargetHighlightsForTextEdit = ({
  highlights = [],
  previousText = "",
  nextText = "",
} = {}) => {
  const list = Array.isArray(highlights) ? highlights : [];
  const before = String(previousText || "");
  const after = String(nextText || "");
  if (!list.length || before === after) return { highlights: list, renamed: {} };

  const shortest = Math.min(before.length, after.length);
  let prefix = 0;
  while (prefix < shortest && before[prefix] === after[prefix]) prefix += 1;
  let suffix = 0;
  while (
    suffix < shortest - prefix &&
    before[before.length - 1 - suffix] === after[after.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  const beforeEnd = before.length - suffix;
  const afterEnd = after.length - suffix;
  const delta = after.length - before.length;
  // Outside the replaced span a position keeps its text; inside it, a range
  // start collapses to the span start and a range end to the span end.
  const mapStart = (index) =>
    index <= prefix ? index : index >= beforeEnd ? index + delta : prefix;
  const mapEnd = (index) =>
    index <= prefix ? index : index >= beforeEnd ? index + delta : afterEnd;
  const isWordChar = (char) => Boolean(char) && /[\p{L}\p{N}]/u.test(char);
  // Typing inside or against a styled word grows the run with it, instead of
  // leaving the highlight cutting the word in half.
  const growStart = (index) => {
    let next = index;
    while (next > 0 && isWordChar(after[next - 1]) && isWordChar(after[next])) {
      next -= 1;
    }
    return next;
  };
  const growEnd = (index) => {
    let next = index;
    while (
      next < after.length &&
      isWordChar(after[next]) &&
      isWordChar(after[next - 1])
    ) {
      next += 1;
    }
    return next;
  };

  const renamed = {};
  const nextHighlights = [];
  list.forEach((slot) => {
    const phrase = normalizeHighlightPhrase(slot?.example);
    if (!phrase) {
      nextHighlights.push(slot);
      return;
    }
    // Overlapping OR abutting the edit: an insertion at the very edge of a
    // styled word reads as growing that word.
    const touched = findAllOccurrences({ text: before, phrase }).find(
      (range) => range.end >= prefix && range.start <= beforeEnd
    );
    if (!touched) {
      if (isHighlightPhraseInText({ text: after, phrase })) nextHighlights.push(slot);
      return;
    }
    const start = growStart(
      Math.max(0, Math.min(mapStart(touched.start), after.length))
    );
    const end = growEnd(Math.max(start, Math.min(mapEnd(touched.end), after.length)));
    const example = normalizeHighlightPhrase(after.slice(start, end));
    if (!example) return;
    if (example === phrase) {
      nextHighlights.push(slot);
      return;
    }
    renamed[phrase] = example;
    nextHighlights.push({ ...slot, example });
  });
  return { highlights: nextHighlights, renamed };
};
